/**
 * Transform frontend event data structure to backend format
 * @param {Object} frontendData - Frontend event data structure
 * @returns {Object} Backend-compatible event data
 */
export const transformEventDataForBackend = (frontendData) => {
  if (!frontendData || typeof frontendData !== 'object') {
    return getDefaultBackendData();
  }

  return {
    name: frontendData.title || frontendData.name || '',
    subtitle: frontendData.subtitle || '',
    // Persist customizable hero title color if set
    eventTitleColor: frontendData.eventTitleColor || '',
    description: frontendData.description || '',
    date: frontendData.date || '',
    time: frontendData.time || '',
    location: frontendData.location || '',
    price: frontendData.price || (frontendData.capacity ? `From R${frontendData.capacity}` : ''),
    attendees: frontendData.capacity 
      ? `${frontendData.capacity}+ Expected` 
      : (frontendData.attendees || ''),
    bannerImage: frontendData.bannerImage || '',
    logoImage: frontendData.logoImage || '',
    speakers: (Array.isArray(frontendData.speakers) ? frontendData.speakers : []).map((speaker) => ({
      name: speaker?.name || '',
      title: speaker?.title || '',
      company: speaker?.company || '',
      image: speaker?.image || '',
      topic: speaker?.topic || '',
      bio: speaker?.bio || '',
      avatar: speaker?.avatar || '',
    })),
    schedule: Array.isArray(frontendData.schedule) ? frontendData.schedule : [],
    // Simple agenda items for in-person events
    agenda: Array.isArray(frontendData.agenda) ? frontendData.agenda : [],
    sponsors: Array.isArray(frontendData.sponsors) ? frontendData.sponsors : [],
    features: Array.isArray(frontendData.features) ? frontendData.features : [],
    videoLink: frontendData.videoLink || '',
  };
};

/**
 * Transform backend event data structure to frontend format
 * @param {Object} backendData - Backend event data structure
 * @returns {Object|null} Frontend-compatible event data or null if invalid
 */
export const transformEventDataFromBackend = (backendData) => {
  if (!backendData || typeof backendData !== 'object') return null;

  return {
    title: backendData.name || backendData.title || '',
    subtitle: backendData.subtitle || '',
    // Restore customizable hero title color, with empty fallback so UI can decide default
    eventTitleColor: backendData.eventTitleColor || '',
    description: backendData.description || '',
    date: backendData.date || '',
    time: backendData.time || '',
    location: backendData.location || '',
    capacity: backendData.attendees 
      ? String(backendData.attendees).replace(/\D/g, '') 
      : (backendData.capacity || ''),
    price: backendData.price || '',
    attendees: backendData.attendees || '',
    ctaText: backendData.ctaText || 'Register Now',
    bannerImage: backendData.bannerImage || '',
    logoImage: backendData.logoImage || '',
    features: Array.isArray(backendData.features) 
      ? backendData.features 
      : [
          'Keynote Speakers',
          'Interactive Workshops',
          'Networking Sessions',
          'Tech Exhibitions',
        ],
    speakers: (Array.isArray(backendData.speakers) ? backendData.speakers : []).map((speaker) => ({
      name: speaker?.name || '',
      title: speaker?.title || '',
      company: speaker?.company || '',
      image: speaker?.image || '',
      topic: speaker?.topic || '',
      bio: speaker?.bio || '',
      avatar: speaker?.avatar || (speaker?.name ? speaker.name.charAt(0) : '?'),
    })),
    schedule: Array.isArray(backendData.schedule) ? backendData.schedule : [],
    agenda: Array.isArray(backendData.agenda) ? backendData.agenda : [],
    sponsors: Array.isArray(backendData.sponsors) ? backendData.sponsors : [],
    videoLink: backendData.videoLink || '',
    supportMail: backendData.supportMail || '',
    supportPhoneNumber: backendData.supportPhoneNumber || '',
  };
};

/**
 * Get default backend data structure
 * @returns {Object} Default backend event data
 */
export const getDefaultBackendData = () => {
  return {
    name: '',
    description: '',
    date: '',
    time: '',
    location: '',
    price: '',
    attendees: '',
    bannerImage: '',
    logoImage: '',
    speakers: [],
    schedule: [],
    agenda: [],
    sponsors: [],
    features: [],
  };
};

